import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { api } from '../api/client';
import { ASSET_CATEGORIES } from '../api/types';
import { EVENT_TYPE_ICON, EVENT_TYPES, type EventType } from '../content/occasions';
import { en } from '../i18n/en';
import { ml } from '../i18n/ml';
import { useT, type Key } from '../i18n';
import { SEATING_LAYOUTS, seatingLayoutKey } from './seating';
import { VENUE_TYPES, canonicalVenueType } from './venueTypes';

/**
 * The lists admins keep (server: /api/master, table master_items): venue
 * types, event types, seating layouts, asset categories and amenities.
 *
 * Every form and filter reads its options from here. `value` is what venues,
 * bookings and assets store; it never changes, so renaming an item only
 * changes its label. A removed item is `active: false`: forms stop offering
 * it, but anything that already uses it still shows its label.
 *
 * Until the server answers (and on a server without migration 026) the lists
 * are the ones the code shipped with, so nothing renders empty.
 */
export type MasterList = 'venue_type' | 'event_type' | 'seating_layout' | 'asset_category' | 'amenity';

export interface MasterItem {
  value: string;
  label_en: string;
  label_ml: string;
  icon: string | null;
  active: boolean;
}

const pick = (key: Key) => ({
  label_en: String(en[key] ?? key),
  label_ml: typeof ml[key] === 'string' ? (ml[key] as string) : '',
});

const EVENT_KEY = (e: string) => `ev${e[0].toUpperCase()}${e.slice(1)}` as Key;
const ASSET_KEY: Record<string, Key> = {
  furniture: 'astCatFurniture', audio_visual: 'astCatAudioVisual', lighting: 'astCatLighting',
  electrical: 'astCatElectrical', climate: 'astCatClimate', kitchen: 'astCatKitchen', decor: 'astCatDecor',
  safety: 'astCatSafety', cleaning: 'astCatCleaning', other: 'astCatOther',
};
const AMENITIES = ['AC', 'Stage', 'Sound System', 'DJ', 'Projector', 'WiFi', 'Power Backup', 'Bridal Room',
  'Green Room', 'Catering', 'Restrooms', 'Wheelchair Access', 'Whiteboard', 'Mirrors'];

const plain = (value: string): MasterItem => ({ value, label_en: value, label_ml: '', icon: null, active: true });

export const BUILT_IN: Record<MasterList, MasterItem[]> = {
  venue_type: VENUE_TYPES.map(plain),
  event_type: EVENT_TYPES.map((e) => ({ value: e, ...pick(EVENT_KEY(e)), icon: EVENT_TYPE_ICON[e], active: true })),
  seating_layout: SEATING_LAYOUTS.map((s) => ({ value: s, ...pick(seatingLayoutKey(s) as Key), icon: null, active: true })),
  asset_category: ASSET_CATEGORIES.map((a) => ({ value: a, ...pick(ASSET_KEY[a]), icon: null, active: true })),
  amenity: AMENITIES.map(plain),
};

export const masterKeys = { all: ['master'] as const };

export function useMaster() {
  const { lang } = useT();
  const { data } = useQuery({
    queryKey: masterKeys.all,
    queryFn: () => api<{ lists: Record<MasterList, MasterItem[]> }>('/master'),
    select: (d) => d.lists,
    staleTime: 5 * 60_000,
  });
  const lists = data ?? BUILT_IN;

  /** The items a form may offer now (removed ones left out). */
  const options = useCallback((list: MasterList) => (lists[list] ?? []).filter((i) => i.active), [lists]);

  const find = useCallback(
    (list: MasterList, value: string | null | undefined) => {
      if (!value) return undefined;
      // Case-blind: venues typed their amenities ("ac") before the list existed.
      const want = (list === 'venue_type' ? canonicalVenueType(value) : value).trim().toLowerCase();
      const match = (i: MasterItem) => i.value.toLowerCase() === want;
      return (lists[list] ?? []).find(match) ?? BUILT_IN[list].find(match);
    },
    [lists],
  );

  /** A stored value as words in the page's language; unknown values are shown as stored. */
  const label = useCallback(
    (list: MasterList, value: string | null | undefined) => {
      const item = find(list, value);
      if (!item) return value ?? '';
      return lang === 'ml' && item.label_ml ? item.label_ml : item.label_en;
    },
    [find, lang],
  );

  /** An event type's icon; a type added by an admin without one gets a plain calendar. */
  const icon = useCallback(
    (value: string | null | undefined) => find('event_type', value)?.icon || 'ri-calendar-event-line',
    [find],
  );

  return useMemo(() => ({ lists, options, find, label, icon }), [lists, options, find, label, icon]);
}

/** True for one of the event types the code ships content for (photos, guides, "good for"). */
export const isBuiltInEvent = (v: string): v is EventType => (EVENT_TYPES as readonly string[]).includes(v);
